import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  cvService,
  type CVState,
  type DocumentAnalysisState,
  type AnalysisDetails,
  type HistoryRecord,
  type SuggestionItem,
} from '../services/cv.service';

type TabKey = 'cv' | 'linkedin' | 'compare';
type ViewKey = 'overview' | 'suggestions' | 'rewrite' | 'chat' | 'history';

/* ─── Utilities ─────────────────────────────────────────────── */

function getScoreLabel(score: number) {
  if (score >= 8) return { text: 'Strong positioning', color: 'text-emerald-400' as const, bg: 'bg-emerald-400/10' as const, border: 'border-emerald-400/20' as const };
  if (score >= 5) return { text: 'Usable but leaking opportunities', color: 'text-amber-400' as const, bg: 'bg-amber-400/10' as const, border: 'border-amber-400/20' as const };
  return { text: 'Needs serious correction', color: 'text-red-400' as const, bg: 'bg-red-400/10' as const, border: 'border-red-400/20' as const };
}

function priorityOrder(p: SuggestionItem['priority']) {
  const map: Record<string, number> = { critical: 0, high: 1, medium: 2, low: 3 };
  return map[p] ?? 2;
}

function priorityStyle(p: SuggestionItem['priority']) {
  switch (p) {
    case 'critical': return 'bg-red-400/15 text-red-400 border-red-400/25';
    case 'high': return 'bg-amber-400/15 text-amber-400 border-amber-400/25';
    case 'medium': return 'bg-primary/10 text-primary border-primary/20';
    case 'low': return 'bg-surface-container-high text-on-surface-variant border-outline-variant/20';
    default: return 'bg-surface-container-high text-on-surface-variant border-outline-variant/20';
  }
}

function categoryIcon(category: string) {
  const map: Record<string, string> = {
    'ATS Readiness': 'search',
    'Content Quality': 'edit_note',
    'Impact & Metrics': 'trending_up',
    Keywords: 'key',
    'Format & Structure': 'format_align_left',
    Headline: 'title',
    'About/Summary': 'person',
    Experience: 'work_history',
    International: 'language',
    Network: 'hub',
    General: 'lightbulb',
  };
  return map[category] ?? 'lightbulb';
}

function useActionItems(kind: TabKey) {
  const key = `cv-actions-${kind}`;
  const [checked, setChecked] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw ? new Set(JSON.parse(raw)) : new Set<string>();
    } catch {
      return new Set<string>();
    }
  });

  useEffect(() => {
    localStorage.setItem(key, JSON.stringify(Array.from(checked)));
  }, [checked, key]);

  const toggle = (id: string) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clear = () => setChecked(new Set());

  return { checked, toggle, clear };
}

/* ─── Animated Score Ring ───────────────────────────────────── */

function ScoreRing({ score, max = 10, size = 96 }: { score: number; max?: number; size?: number }) {
  const radius = size === 96 ? 40 : 28;
  const stroke = size === 96 ? 7 : 5;
  const circumference = 2 * Math.PI * radius;
  const filled = circumference * (score / max);
  const color = score >= 8 ? '#4ade80' : score >= 5 ? '#fbbf24' : '#f87171';

  return (
    <div className="relative flex items-center justify-center" style={{ width: size, height: size }}>
      <svg className="absolute -rotate-90" width={size} height={size}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#2a2a2c" strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={`${filled} ${circumference}`} strokeLinecap="round"
          style={{ transition: 'stroke-dasharray 1s ease-out' }}
        />
      </svg>
      <div className="text-center">
        <span className="block text-3xl font-extrabold text-on-surface" style={{ fontSize: size === 96 ? undefined : '1.25rem' }}>{score}</span>
        {size === 96 && <span className="text-[10px] font-bold uppercase tracking-widest text-on-surface-variant">/ {max}</span>}
      </div>
    </div>
  );
}

/* ─── Category Score Bar ────────────────────────────────────── */

function CategoryBar({ label, score, icon }: { label: string; score: number; icon: string }) {
  const color = score >= 8 ? 'bg-emerald-400' : score >= 5 ? 'bg-amber-400' : 'bg-red-400';
  return (
    <div className="flex items-center gap-3">
      <span className="material-symbols-outlined text-on-surface-variant text-lg">{icon}</span>
      <div className="flex-1 min-w-0">
        <div className="flex justify-between mb-1">
          <span className="text-xs font-medium text-on-surface">{label}</span>
          <span className="text-xs font-bold text-on-surface-variant">{score}/10</span>
        </div>
        <div className="h-2 rounded-full bg-surface-container-high overflow-hidden">
          <div
            className={`h-full rounded-full ${color} transition-all duration-1000 ease-out`}
            style={{ width: `${score * 10}%` }}
          />
        </div>
      </div>
    </div>
  );
}

/* ─── Copy Button ───────────────────────────────────────────── */

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <button
      onClick={handleCopy}
      className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold text-on-surface-variant hover:text-primary transition-colors"
    >
      <span className="material-symbols-outlined text-xs">{copied ? 'check' : 'content_copy'}</span>
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

/* ─── Skeleton ──────────────────────────────────────────────── */

function Sk({ className, style }: { className?: string; style?: React.CSSProperties }) {
  return <div className={`animate-pulse rounded-lg bg-surface-container-high ${className ?? ''}`} style={style} />;
}

function ScoreDashboardSkeleton() {
  return (
    <div className="rounded-2xl bg-surface-container-low p-5 sm:p-6 border border-outline-variant/10">
      <div className="flex flex-col sm:flex-row items-center gap-6">
        {/* Score ring placeholder */}
        <Sk className="shrink-0 w-24 h-24 rounded-full" />
        <div className="flex-1 w-full space-y-3">
          <Sk className="h-6 w-36 rounded-full" />
          <Sk className="h-3 w-48" />
          <div className="space-y-3 pt-2">
            {[72, 60, 80, 56, 68].map((w, i) => (
              <div key={i} className="flex items-center gap-3">
                <Sk className="w-5 h-5 rounded-full shrink-0" />
                <div className="flex-1 space-y-1.5">
                  <div className="flex justify-between">
                    <Sk className="h-3" style={{ width: `${w}%` }} />
                    <Sk className="h-3 w-8" />
                  </div>
                  <Sk className="h-2 w-full rounded-full" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function AnalysisContentSkeleton() {
  return (
    <div className="space-y-4 animate-in fade-in duration-300">
      {/* Red flags card */}
      <div className="rounded-2xl border border-outline-variant/10 bg-surface-container-low p-5 space-y-3">
        <Sk className="h-4 w-32" />
        {[90, 75, 85].map((w, i) => (
          <div key={i} className="flex gap-3 items-start">
            <Sk className="w-4 h-4 rounded-full shrink-0 mt-0.5" />
            <Sk className="h-3 flex-1" style={{ width: `${w}%` }} />
          </div>
        ))}
      </div>
      {/* Quick wins card */}
      <div className="rounded-2xl border border-outline-variant/10 bg-surface-container-low p-5 space-y-3">
        <Sk className="h-4 w-28" />
        {[80, 70, 95, 65].map((w, i) => (
          <div key={i} className="flex gap-3 items-start">
            <Sk className="w-4 h-4 rounded-full shrink-0 mt-0.5" />
            <Sk className="h-3 flex-1" style={{ width: `${w}%` }} />
          </div>
        ))}
      </div>
      {/* Section breakdown */}
      <div className="rounded-2xl border border-outline-variant/10 bg-surface-container-low p-5 space-y-3">
        <Sk className="h-4 w-40" />
        <div className="grid gap-3 sm:grid-cols-2">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="rounded-xl bg-surface-container p-4 border border-outline-variant/10 space-y-2">
              <div className="flex justify-between">
                <Sk className="h-3 w-20" />
                <Sk className="h-3 w-8" />
              </div>
              <Sk className="h-3 w-full" />
              <Sk className="h-3 w-4/5" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function AnalysisSkeleton({ label }: { label?: string }) {
  return (
    <div className="space-y-5">
      <ScoreDashboardSkeleton />
      {/* View tab pills */}
      <div className="flex flex-wrap gap-2">
        {[88, 100, 72, 68, 76].map((w, i) => (
          <Sk key={i} className="h-10 rounded-xl" style={{ width: w }} />
        ))}
      </div>
      {label && (
        <p className="text-xs text-on-surface-variant animate-pulse text-center py-1">{label}</p>
      )}
      <AnalysisContentSkeleton />
    </div>
  );
}

function UploadPanelSkeleton() {
  return (
    <div className="rounded-2xl bg-surface-container-low p-5 border border-outline-variant/10 space-y-4">
      <Sk className="h-3 w-24" />
      <Sk className="h-3 w-full" />
      <Sk className="h-3 w-3/4" />
      <div className="rounded-xl border-2 border-dashed border-outline-variant p-6 flex flex-col items-center gap-3">
        <Sk className="w-10 h-10 rounded-full" />
        <Sk className="h-3 w-36" />
        <Sk className="h-3 w-24" />
      </div>
    </div>
  );
}

/* ─── Upload Panel ──────────────────────────────────────────── */

function UploadPanel({
  title,
  description,
  accept,
  hint,
  fileName,
  isPending,
  error,
  onSelect,
}: {
  title: string;
  description: string;
  accept: string;
  hint: string;
  fileName?: string | null;
  isPending: boolean;
  error?: string;
  onSelect: (file: File) => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const acceptedExts = accept.split(',').map((e) => e.trim().toLowerCase());

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setIsDragging(false);
    if (isPending) return;
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    const ext = '.' + file.name.split('.').pop()?.toLowerCase();
    if (!acceptedExts.includes(ext)) return;
    onSelect(file);
  }

  return (
    <div className="rounded-2xl bg-surface-container-low p-5 border border-outline-variant/10">
      <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-primary">{title}</p>
      <p className="mb-4 text-xs leading-relaxed text-on-surface-variant">{description}</p>
      <input
        ref={fileInputRef}
        type="file"
        accept={accept}
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) onSelect(file);
          e.currentTarget.value = '';
        }}
        className="hidden"
      />
      {isPending ? (
        <div className="rounded-xl border-2 border-dashed border-primary/40 bg-primary/5 p-4 space-y-3 animate-pulse">
          <div className="flex items-center gap-3">
            <div className="h-6 w-6 shrink-0 animate-spin rounded-full border-2 border-primary/40 border-t-primary" />
            <div className="flex-1 space-y-2">
              <div className="h-3 rounded-lg bg-surface-container-high w-3/4" />
              <div className="h-2 rounded-lg bg-surface-container-high w-1/2" />
            </div>
          </div>
          <p className="text-xs text-primary/70 text-center">Uploading and analyzing…</p>
        </div>
      ) : fileName ? (
        <div
          className={`rounded-xl border-2 border-dashed p-4 transition-colors ${isDragging ? 'border-primary bg-primary/10' : 'border-outline-variant'}`}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <div className="flex items-center justify-between gap-3">
            <div className="flex min-w-0 items-center gap-3">
              <span className="material-symbols-outlined shrink-0 text-2xl text-primary">description</span>
              <div className="min-w-0">
                <p className="truncate text-sm font-medium text-on-surface">{fileName}</p>
                <p className="text-xs text-on-surface-variant">{isDragging ? 'Drop to replace' : hint}</p>
              </div>
            </div>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="shrink-0 text-xs text-primary hover:underline"
            >
              Replace
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          className={`w-full rounded-xl border-2 border-dashed p-6 transition-colors ${
            isDragging ? 'border-primary bg-primary/10' : 'border-outline-variant hover:border-primary hover:bg-primary/5'
          }`}
        >
          <div className="flex flex-col items-center gap-3">
            <span className={`material-symbols-outlined text-4xl transition-colors ${isDragging ? 'text-primary' : 'text-on-surface-variant'}`}>
              {isDragging ? 'download' : 'upload_file'}
            </span>
            <div className="text-center">
              <p className="text-sm font-medium text-on-surface">
                {isDragging ? 'Drop to upload' : 'Click or drag to upload'}
              </p>
              <p className="mt-1 text-xs text-on-surface-variant">{hint}</p>
            </div>
          </div>
        </button>
      )}
      {error && <p className="mt-3 text-xs text-error">{error}</p>}
    </div>
  );
}

/* ─── Overview Panel ────────────────────────────────────────── */

function OverviewPanel({ analysis, kind }: { analysis: DocumentAnalysisState; kind: string }) {
  const d = analysis.details;
  const hasDetails = !!d;

  return (
    <div className="space-y-5 animate-in fade-in duration-500">
      {/* Red Flags */}
      {analysis.redFlags.length > 0 && (
        <div className="rounded-2xl border border-error/20 bg-error/10 p-5">
          <h3 className="mb-4 flex items-center gap-2 font-bold text-error text-sm">
            <span className="material-symbols-outlined text-lg">flag</span>
            Critical Red Flags
          </h3>
          <ul className="space-y-3">
            {analysis.redFlags.map((flag, i) => (
              <li key={i} className="flex gap-3 text-sm">
                <span className="material-symbols-outlined mt-0.5 shrink-0 text-base text-error">cancel</span>
                <span className="text-on-surface-variant leading-relaxed">{flag}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Quick Wins */}
      {hasDetails && d!.quickWins.length > 0 && (
        <div className="rounded-2xl border border-emerald-400/20 bg-emerald-400/10 p-5">
          <h3 className="mb-4 flex items-center gap-2 font-bold text-emerald-400 text-sm">
            <span className="material-symbols-outlined text-lg">bolt</span>
            Quick Wins
          </h3>
          <ul className="space-y-3">
            {d!.quickWins.map((win, i) => (
              <li key={i} className="flex gap-3 text-sm">
                <span className="material-symbols-outlined mt-0.5 shrink-0 text-base text-emerald-400">check_circle</span>
                <span className="text-on-surface-variant leading-relaxed">{win}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Keyword Gaps */}
      {hasDetails && d!.keywordGaps.length > 0 && (
        <div className="rounded-2xl border border-primary/20 bg-primary/5 p-5">
          <h3 className="mb-4 flex items-center gap-2 font-bold text-primary text-sm">
            <span className="material-symbols-outlined text-lg">key</span>
            Keyword Gaps
          </h3>
          <div className="flex flex-wrap gap-2">
            {d!.keywordGaps.map((gap, i) => (
              <span key={i} className="inline-flex items-center gap-1 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-medium text-primary">
                {gap}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Section Feedback */}
      {hasDetails && d!.sections.length > 0 && (
        <div className="rounded-2xl bg-surface-container-low p-5 border border-outline-variant/10">
          <h3 className="mb-4 flex items-center gap-2 font-bold text-on-surface text-sm">
            <span className="material-symbols-outlined text-lg text-tertiary">segment</span>
            Section Breakdown
          </h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {d!.sections.map((sec, i) => {
              const scoreColor = sec.score >= 8 ? 'text-emerald-400' : sec.score >= 5 ? 'text-amber-400' : 'text-red-400';
              return (
                <div key={i} className="rounded-xl bg-surface-container p-4 border border-outline-variant/10">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-semibold text-on-surface">{sec.name}</span>
                    <span className={`text-xs font-bold ${scoreColor}`}>{sec.score}/10</span>
                  </div>
                  <p className="text-xs text-on-surface-variant leading-relaxed">{sec.feedback}</p>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Verdict */}
      {analysis.verdict && (
        <div className="rounded-2xl bg-surface-container-low p-5 border border-outline-variant/10">
          <h3 className="mb-3 flex items-center gap-2 font-bold text-on-surface text-sm">
            <span className="material-symbols-outlined text-lg text-primary">gavel</span>
            Final Verdict
          </h3>
          <p className="text-sm leading-relaxed text-on-surface-variant">{analysis.verdict}</p>
        </div>
      )}
    </div>
  );
}

/* ─── Suggestions Panel ─────────────────────────────────────── */

function SuggestionsPanel({ analysis, kind }: { analysis: DocumentAnalysisState; kind: string }) {
  const d = analysis.details;
  const items: SuggestionItem[] = d?.suggestions.length
    ? d.suggestions
    : analysis.suggestions.map((s) => ({ text: s, category: 'General', priority: 'medium' as const }));

  const sorted = useMemo(() => [...items].sort((a, b) => priorityOrder(a.priority) - priorityOrder(b.priority)), [items]);
  const { checked, toggle, clear } = useActionItems(kind as TabKey);
  const progress = sorted.length > 0 ? Math.round((Array.from(checked).filter((id) => sorted.some((s) => id === `${kind}-${s.text}`)).length / sorted.length) * 100) : 0;

  if (sorted.length === 0) {
    return (
      <div className="flex min-h-48 items-center justify-center rounded-2xl bg-surface-container-low p-8 text-center border border-outline-variant/10">
        <p className="text-sm text-on-surface-variant">No suggestions yet. Upload a file to get started.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-in fade-in duration-500">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className="text-xs font-bold uppercase tracking-widest text-on-surface-variant">Progress</span>
          <div className="h-2 w-32 rounded-full bg-surface-container-high overflow-hidden">
            <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${progress}%` }} />
          </div>
          <span className="text-xs font-bold text-on-surface">{progress}%</span>
        </div>
        {checked.size > 0 && (
          <button onClick={clear} className="text-xs text-on-surface-variant hover:text-error transition-colors">
            Reset
          </button>
        )}
      </div>

      <div className="space-y-3">
        {sorted.map((s, i) => {
          const id = `${kind}-${s.text}`;
          const isDone = checked.has(id);
          return (
            <div
              key={i}
              className={`group rounded-2xl border p-4 transition-all ${isDone ? 'border-outline-variant/10 bg-surface-container-low/50 opacity-60' : 'border-outline-variant/20 bg-surface-container-low hover:border-primary/30'}`}
            >
              <div className="flex items-start gap-3">
                <button
                  onClick={() => toggle(id)}
                  className={`mt-0.5 shrink-0 flex h-5 w-5 items-center justify-center rounded-md border transition-colors ${
                    isDone ? 'bg-primary border-primary' : 'border-outline-variant hover:border-primary'
                  }`}
                >
                  {isDone && <span className="material-symbols-outlined text-xs text-on-primary">check</span>}
                </button>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm leading-relaxed ${isDone ? 'line-through text-on-surface-variant/60' : 'text-on-surface-variant'}`}>
                    {s.text}
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${priorityStyle(s.priority)}`}>
                      {s.priority}
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full border border-outline-variant/10 bg-surface-container-high px-2 py-0.5 text-[10px] font-medium text-on-surface-variant">
                      <span className="material-symbols-outlined text-[10px]">{categoryIcon(s.category)}</span>
                      {s.category}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Rewrite Panel ─────────────────────────────────────────── */

function RewritePanel({ analysis }: { analysis: DocumentAnalysisState }) {
  if (!analysis.improvedText) {
    return (
      <div className="flex min-h-48 items-center justify-center rounded-2xl bg-surface-container-low p-8 text-center border border-outline-variant/10">
        <p className="text-sm text-on-surface-variant">No rewrite available yet.</p>
      </div>
    );
  }

  return (
    <div className="animate-in fade-in duration-500 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-2 font-bold text-on-surface text-sm">
          <span className="material-symbols-outlined text-lg text-primary">edit_document</span>
          Suggested Rewrite
        </h3>
        <CopyButton text={analysis.improvedText} />
      </div>
      <div className="rounded-2xl border border-primary/20 bg-primary/5 p-5">
        <pre className="whitespace-pre-wrap break-words text-sm leading-relaxed text-on-surface-variant font-sans">
          {analysis.improvedText}
        </pre>
      </div>
    </div>
  );
}

/* ─── Chat Panel ────────────────────────────────────────────── */

interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  isLoading?: boolean;
}

function ChatPanel({ kind, analysis }: { kind: 'resume' | 'linkedin'; analysis: DocumentAnalysisState }) {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const send = async () => {
    const text = input.trim();
    if (!text || isLoading) return;
    const userMsg: ChatMessage = { id: crypto.randomUUID(), role: 'user', content: text };
    const assistantId = crypto.randomUUID();
    setMessages((prev) => [...prev, userMsg, { id: assistantId, role: 'assistant', content: '', isLoading: true }]);
    setInput('');
    setIsLoading(true);

    try {
      const history = messages
        .filter((m) => !m.isLoading)
        .map((m) => ({ role: m.role as 'user' | 'assistant', content: m.content }));
      const res = await cvService.chat(text, history, kind);
      setMessages((prev) =>
        prev.map((m) => (m.id === assistantId ? { ...m, content: res.content, isLoading: false } : m)),
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to get response';
      setMessages((prev) =>
        prev.map((m) => (m.id === assistantId ? { ...m, content: `Error: ${msg}`, isLoading: false } : m)),
      );
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="flex flex-col h-[500px] rounded-2xl border border-outline-variant/10 bg-surface-container-low overflow-hidden animate-in fade-in duration-500">
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center px-4">
            <span className="material-symbols-outlined text-4xl text-on-surface-variant/30 mb-3">forum</span>
            <p className="text-sm font-medium text-on-surface">Ask about your {kind === 'resume' ? 'CV' : 'LinkedIn'}</p>
            <p className="text-xs text-on-surface-variant mt-1 max-w-xs">
              "Rewrite my summary for a senior role", "Why is this a red flag?", "How do I fix the keyword gaps?"
            </p>
          </div>
        )}
        {messages.map((msg) => (
          <div key={msg.id} className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
            <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${msg.role === 'user' ? 'bg-surface-container-highest' : 'primary-gradient'}`}>
              <span className={`material-symbols-outlined text-xs ${msg.role === 'user' ? 'text-on-surface-variant' : 'text-on-primary'}`}>
                {msg.role === 'user' ? 'person' : 'auto_awesome'}
              </span>
            </div>
            <div className={`max-w-[80%] rounded-xl px-4 py-3 text-sm leading-relaxed ${
              msg.role === 'user'
                ? 'bg-surface-container-high text-on-surface rounded-tr-none'
                : 'bg-surface-container text-on-surface-variant rounded-tl-none border border-outline-variant/10'
            }`}>
              {msg.isLoading ? (
                <span className="inline-flex gap-1">
                  <span className="w-1.5 h-1.5 bg-on-surface-variant rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-1.5 h-1.5 bg-on-surface-variant rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-1.5 h-1.5 bg-on-surface-variant rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </span>
              ) : (
                msg.content
              )}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>
      <div className="p-3 border-t border-outline-variant/10 bg-surface-container-low">
        <div className="flex items-center gap-2">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
            placeholder={`Ask about your ${kind === 'resume' ? 'CV' : 'LinkedIn'}...`}
            disabled={isLoading}
            className="flex-1 min-w-0 bg-surface-container-high rounded-xl px-4 py-2.5 text-sm text-on-surface placeholder:text-on-surface-variant/40 border border-outline-variant/10 focus:border-primary/40 focus:outline-none transition-colors"
          />
          <button
            onClick={send}
            disabled={!input.trim() || isLoading}
            className="w-10 h-10 shrink-0 rounded-xl primary-gradient text-on-primary flex items-center justify-center disabled:opacity-40 transition-opacity hover:opacity-90"
          >
            <span className="material-symbols-outlined text-sm" style={{ fontVariationSettings: "'FILL' 1" }}>arrow_upward</span>
          </button>
        </div>
      </div>
    </div>
  );
}

/* ─── History Panel ─────────────────────────────────────────── */

function HistoryPanel({ kind }: { kind: 'resume' | 'linkedin' }) {
  const { data: history, isLoading } = useQuery<HistoryRecord[]>({
    queryKey: ['cv-history', kind],
    queryFn: () => cvService.getHistory(kind),
  });

  if (isLoading) {
    return (
      <div className="space-y-3 animate-in fade-in duration-300">
        {[1, 2, 3].map((i) => (
          <div key={i} className="rounded-2xl bg-surface-container-low p-4 border border-outline-variant/10 flex items-center gap-4 animate-pulse">
            <Sk className="w-16 h-16 rounded-full shrink-0" />
            <div className="flex-1 space-y-2">
              <Sk className="h-3 w-3/4" />
              <Sk className="h-3 w-1/2" />
              <Sk className="h-2 w-2/3" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (!history || history.length === 0) {
    return (
      <div className="flex min-h-48 items-center justify-center rounded-2xl bg-surface-container-low p-8 text-center border border-outline-variant/10">
        <p className="text-sm text-on-surface-variant">No history yet. Upload a file to create your first analysis.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3 animate-in fade-in duration-500">
      {history.map((h) => {
        const label = getScoreLabel(h.score ?? 0);
        return (
          <div key={h.id} className="rounded-2xl bg-surface-container-low p-4 border border-outline-variant/10 flex items-center gap-4">
            <div className="shrink-0">
              <ScoreRing score={h.score ?? 0} size={64} />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-on-surface truncate">{h.fileName ?? 'Untitled'}</p>
              <p className={`text-xs font-semibold mt-0.5 ${label.color}`}>{label.text}</p>
              <p className="text-[10px] text-on-surface-variant/60 mt-1">
                {new Date(h.createdAt).toLocaleDateString()} · {h.redFlags.length} flags · {h.suggestions.length} suggestions
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ─── Score Dashboard ───────────────────────────────────────── */

function ScoreDashboard({ analysis }: { analysis: DocumentAnalysisState }) {
  const label = getScoreLabel(analysis.score ?? 0);
  const d = analysis.details;

  return (
    <div className="rounded-2xl bg-surface-container-low p-5 sm:p-6 border border-outline-variant/10">
      <div className="flex flex-col sm:flex-row items-center gap-6">
        <ScoreRing score={analysis.score ?? 0} />
        <div className="flex-1 w-full">
          <div className="text-center sm:text-left mb-4">
            <span className={`inline-flex items-center rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-widest ${label.bg} ${label.color} ${label.border}`}>
              {label.text}
            </span>
            {analysis.reviewedAt && (
              <p className="mt-2 text-xs text-on-surface-variant">
                Last analyzed {new Date(analysis.reviewedAt).toLocaleString()}
              </p>
            )}
          </div>
          {d && d.categoryScores.length > 0 && (
            <div className="space-y-3">
              {d.categoryScores.map((c, i) => (
                <CategoryBar key={i} label={c.category} score={c.score} icon={categoryIcon(c.category)} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Comparison View ───────────────────────────────────────── */

function ComparisonView({ cv, linkedin }: { cv: DocumentAnalysisState; linkedin: DocumentAnalysisState }) {
  const cvLabel = getScoreLabel(cv.score ?? 0);
  const liLabel = getScoreLabel(linkedin.score ?? 0);
  const [compareResult, setCompareResult] = useState<{
    consistencyScore: number;
    insights: string[];
    gaps: string[];
    recommendations: string[];
  } | null>(null);
  const [isComparing, setIsComparing] = useState(false);
  const [compareError, setCompareError] = useState<string | null>(null);

  const runCompare = async () => {
    setIsComparing(true);
    setCompareError(null);
    try {
      const res = await cvService.compareDocuments();
      if (res) setCompareResult(res);
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to compare';
      setCompareError(msg);
    } finally {
      setIsComparing(false);
    }
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      {/* Side-by-side scores */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* CV Side */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="material-symbols-outlined text-primary text-xl">description</span>
            <h2 className="text-lg font-bold text-on-surface">CV Analysis</h2>
            <span className={`ml-auto inline-flex items-center rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-widest ${cvLabel.bg} ${cvLabel.color} ${cvLabel.border}`}>
              {cv.score ?? 0}/10
            </span>
          </div>
          {cv.details && cv.details.categoryScores.length > 0 && (
            <div className="rounded-2xl bg-surface-container-low p-4 border border-outline-variant/10 space-y-3">
              {cv.details.categoryScores.map((c, i) => (
                <CategoryBar key={i} label={c.category} score={c.score} icon={categoryIcon(c.category)} />
              ))}
            </div>
          )}
          {cv.redFlags.length > 0 && (
            <div className="rounded-2xl border border-error/20 bg-error/10 p-4">
              <p className="text-xs font-bold uppercase tracking-widest text-error mb-2">Red Flags</p>
              <ul className="space-y-2">
                {cv.redFlags.map((f, i) => (
                  <li key={i} className="flex gap-2 text-xs text-on-surface-variant">
                    <span className="material-symbols-outlined text-sm text-error shrink-0">cancel</span>
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* LinkedIn Side */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="material-symbols-outlined text-primary text-xl">share</span>
            <h2 className="text-lg font-bold text-on-surface">LinkedIn Analysis</h2>
            <span className={`ml-auto inline-flex items-center rounded-full border px-3 py-1 text-[10px] font-bold uppercase tracking-widest ${liLabel.bg} ${liLabel.color} ${liLabel.border}`}>
              {linkedin.score ?? 0}/10
            </span>
          </div>
          {linkedin.details && linkedin.details.categoryScores.length > 0 && (
            <div className="rounded-2xl bg-surface-container-low p-4 border border-outline-variant/10 space-y-3">
              {linkedin.details.categoryScores.map((c, i) => (
                <CategoryBar key={i} label={c.category} score={c.score} icon={categoryIcon(c.category)} />
              ))}
            </div>
          )}
          {linkedin.redFlags.length > 0 && (
            <div className="rounded-2xl border border-error/20 bg-error/10 p-4">
              <p className="text-xs font-bold uppercase tracking-widest text-error mb-2">Red Flags</p>
              <ul className="space-y-2">
                {linkedin.redFlags.map((f, i) => (
                  <li key={i} className="flex gap-2 text-xs text-on-surface-variant">
                    <span className="material-symbols-outlined text-sm text-error shrink-0">cancel</span>
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* AI Cross-Analysis */}
      <div className="rounded-2xl border border-primary/20 bg-primary/5 p-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <span className="material-symbols-outlined text-primary text-xl">psychology</span>
            <h3 className="text-base font-bold text-on-surface">AI Cross-Analysis</h3>
          </div>
          {!compareResult && (
            <button
              onClick={runCompare}
              disabled={isComparing}
              className="inline-flex items-center gap-1.5 rounded-xl px-4 py-2 primary-gradient text-on-primary text-xs font-bold transition-opacity hover:opacity-90 disabled:opacity-50"
            >
              {isComparing ? (
                <>
                  <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-on-primary/30 border-t-on-primary" />
                  Analyzing…
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined text-sm">auto_awesome</span>
                  Generate Insights
                </>
              )}
            </button>
          )}
        </div>

        {compareError && (
          <p className="text-xs text-error mb-3">{compareError}</p>
        )}

        {!compareResult && !isComparing && (
          <p className="text-sm text-on-surface-variant">
            Compare your CV and LinkedIn profile to uncover inconsistencies, credibility gaps, and quick alignment wins.
          </p>
        )}

        {compareResult && (
          <div className="space-y-5 animate-in fade-in duration-500">
            {/* Consistency Score */}
            <div className="flex items-center gap-4">
              <ScoreRing score={compareResult.consistencyScore} size={72} />
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-on-surface-variant">Consistency Score</p>
                <p className="text-sm text-on-surface-variant mt-1">
                  {compareResult.consistencyScore >= 8
                    ? 'Strong alignment between both documents.'
                    : compareResult.consistencyScore >= 5
                    ? 'Some gaps exist — see below.'
                    : 'Significant inconsistencies detected.'}
                </p>
              </div>
            </div>

            {/* Insights */}
            {compareResult.insights.length > 0 && (
              <div className="rounded-xl bg-surface-container-low p-4 border border-outline-variant/10">
                <p className="text-xs font-bold uppercase tracking-widest text-primary mb-3">Insights</p>
                <ul className="space-y-2">
                  {compareResult.insights.map((item, i) => (
                    <li key={i} className="flex gap-2 text-sm text-on-surface-variant">
                      <span className="material-symbols-outlined text-sm text-primary shrink-0">lightbulb</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Gaps */}
            {compareResult.gaps.length > 0 && (
              <div className="rounded-xl bg-surface-container-low p-4 border border-outline-variant/10">
                <p className="text-xs font-bold uppercase tracking-widest text-amber-400 mb-3">Gaps</p>
                <ul className="space-y-2">
                  {compareResult.gaps.map((item, i) => (
                    <li key={i} className="flex gap-2 text-sm text-on-surface-variant">
                      <span className="material-symbols-outlined text-sm text-amber-400 shrink-0">warning</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Recommendations */}
            {compareResult.recommendations.length > 0 && (
              <div className="rounded-xl bg-surface-container-low p-4 border border-outline-variant/10">
                <p className="text-xs font-bold uppercase tracking-widest text-emerald-400 mb-3">Recommendations</p>
                <ul className="space-y-2">
                  {compareResult.recommendations.map((item, i) => (
                    <li key={i} className="flex gap-2 text-sm text-on-surface-variant">
                      <span className="material-symbols-outlined text-sm text-emerald-400 shrink-0">check_circle</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <button
              onClick={runCompare}
              disabled={isComparing}
              className="text-xs font-bold text-primary hover:underline disabled:opacity-50"
            >
              Regenerate insights
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Main Page ─────────────────────────────────────────────── */

const TAB_COPY: Record<'cv' | 'linkedin', {
  badge: string;
  title: string;
  description: string;
  uploadTitle: string;
  uploadDescription: string;
  hint: string;
  emptyDescription: string;
  accept: string;
}> = {
  cv: {
    badge: 'El Auditor',
    title: 'CV Review',
    description:
      'Upload your resume and get an instant AI audit with actionable fixes, keyword gaps, and a rewritten version.',
    uploadTitle: 'Current CV',
    uploadDescription:
      'Upload the resume you are actually using to apply. Updating the file triggers a fresh analysis automatically.',
    hint: 'PDF or TXT, max 10 MB',
    emptyDescription:
      'Upload your current CV and the latest analysis will be shown here. No previous reviews, only the active version.',
    accept: '.pdf,.txt',
  },
  linkedin: {
    badge: 'LinkedIn PDF',
    title: 'LinkedIn Profile Review',
    description:
      'Upload the PDF exported from LinkedIn. This analysis evaluates profile positioning, keywording and recruiter-readiness.',
    uploadTitle: 'LinkedIn Profile PDF',
    uploadDescription:
      'Download your LinkedIn profile as PDF and upload it here. The audit focuses on headline, about, keyword density and positioning.',
    hint: 'LinkedIn exported PDF, max 10 MB',
    emptyDescription:
      'Upload the PDF exported from LinkedIn to see the current profile audit. This state lives next to your CV but is analyzed separately.',
    accept: '.pdf',
  },
};

const VIEW_TABS: { key: ViewKey; label: string; icon: string }[] = [
  { key: 'overview', label: 'Overview', icon: 'dashboard' },
  { key: 'suggestions', label: 'Action Items', icon: 'checklist' },
  { key: 'rewrite', label: 'Rewrite', icon: 'edit_document' },
  { key: 'chat', label: 'Ask AI', icon: 'forum' },
  { key: 'history', label: 'History', icon: 'history' },
];

export default function CvOptimizer() {
  const [activeTab, setActiveTab] = useState<TabKey>('cv');
  const [view, setView] = useState<ViewKey>('overview');
  const queryClient = useQueryClient();

  const { data: state, isLoading: stateLoading } = useQuery<CVState | null>({
    queryKey: ['cv-state'],
    queryFn: () => cvService.getState(),
  });

  const uploadResumeMutation = useMutation({
    mutationFn: (file: File) => cvService.uploadResume(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cv-state'] });
      queryClient.invalidateQueries({ queryKey: ['cv-history'] });
    },
  });

  const uploadLinkedinMutation = useMutation({
    mutationFn: (file: File) => cvService.uploadLinkedinPdf(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cv-state'] });
      queryClient.invalidateQueries({ queryKey: ['cv-history'] });
    },
  });

  const cvAnalysis = useMemo<DocumentAnalysisState>(() => {
    return state?.cv ?? { redFlags: [], suggestions: [] };
  }, [state]);

  const linkedinAnalysis = useMemo<DocumentAnalysisState>(() => {
    return state?.linkedin ?? { redFlags: [], suggestions: [] };
  }, [state]);

  const currentAnalysis = activeTab === 'cv' ? cvAnalysis : activeTab === 'linkedin' ? linkedinAnalysis : cvAnalysis;
  const hasAnalysis = !!currentAnalysis.fileName && !!currentAnalysis.reviewedAt;
  const tabConfig = activeTab !== 'compare' ? TAB_COPY[activeTab] : null;

  // Reset view to overview when switching tabs
  useEffect(() => {
    setView('overview');
  }, [activeTab]);

  const isUploading =
    (activeTab === 'cv' && uploadResumeMutation.isPending) ||
    (activeTab === 'linkedin' && uploadLinkedinMutation.isPending);

  const renderContent = () => {
    if (stateLoading) {
      return <AnalysisSkeleton />;
    }

    if (activeTab === 'compare') {
      if (!cvAnalysis.fileName && !linkedinAnalysis.fileName) {
        return (
          <div className="flex min-h-80 flex-col items-center justify-center rounded-2xl bg-surface-container-low p-8 text-center border border-outline-variant/10">
            <span className="material-symbols-outlined mb-4 block text-5xl text-on-surface-variant">compare_arrows</span>
            <p className="font-semibold text-on-surface">Upload both documents to compare</p>
            <p className="mt-2 max-w-sm text-sm text-on-surface-variant">Upload your CV and LinkedIn PDF to see a side-by-side comparison.</p>
          </div>
        );
      }
      return <ComparisonView cv={cvAnalysis} linkedin={linkedinAnalysis} />;
    }

    if (isUploading) {
      return <AnalysisSkeleton label="Analyzing your document, this may take a moment…" />;
    }

    if (!hasAnalysis) {
      return (
        <div className="flex min-h-80 flex-col items-center justify-center rounded-2xl bg-surface-container-low p-8 text-center border border-outline-variant/10">
          <span className="material-symbols-outlined mb-4 block text-5xl text-on-surface-variant">rate_review</span>
          <p className="font-semibold text-on-surface">{tabConfig?.title} analysis will appear here</p>
          <p className="mt-2 max-w-sm text-sm text-on-surface-variant">{tabConfig?.emptyDescription}</p>
        </div>
      );
    }

    return (
      <div className="space-y-5">
        <ScoreDashboard analysis={currentAnalysis} />
        <div className="flex flex-wrap gap-2">
          {VIEW_TABS.map((v) => (
            <button
              key={v.key}
              onClick={() => setView(v.key)}
              className={`inline-flex items-center gap-1.5 rounded-xl px-4 py-2.5 text-xs font-bold transition-colors ${
                view === v.key
                  ? 'bg-primary text-background'
                  : 'bg-surface-container-high text-on-surface-variant hover:text-on-surface'
              }`}
            >
              <span className="material-symbols-outlined text-sm">{v.icon}</span>
              {v.label}
            </button>
          ))}
        </div>
        <div>
          {view === 'overview' && <OverviewPanel analysis={currentAnalysis} kind={activeTab} />}
          {view === 'suggestions' && <SuggestionsPanel analysis={currentAnalysis} kind={activeTab} />}
          {view === 'rewrite' && <RewritePanel analysis={currentAnalysis} />}
          {view === 'chat' && <ChatPanel kind={activeTab as 'resume' | 'linkedin'} analysis={currentAnalysis} />}
          {view === 'history' && <HistoryPanel kind={activeTab as 'resume' | 'linkedin'} />}
        </div>
      </div>
    );
  };

  return (
    <div className="mx-auto max-w-7xl space-y-6 px-4 py-6 sm:px-6 sm:py-8 lg:p-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
        <div>
          <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-primary">El Auditor</p>
          <h1 className="text-2xl font-bold text-on-surface sm:text-3xl">CV & LinkedIn Review</h1>
          <p className="mt-2 max-w-2xl text-sm text-on-surface-variant">
            AI-powered audit that goes beyond surface-level feedback. Get category scores, actionable items, keyword gaps, and an AI assistant to iterate on your documents.
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex w-full rounded-xl bg-surface-container p-1 sm:inline-flex sm:w-auto border border-outline-variant/10">
        {(['cv', 'linkedin', 'compare'] as TabKey[]).map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`flex-1 rounded-lg px-5 py-3 text-sm font-semibold transition-colors sm:flex-none inline-flex items-center justify-center gap-2 ${
              activeTab === tab
                ? 'bg-primary text-background'
                : 'text-on-surface-variant hover:text-on-surface'
            }`}
          >
            <span className="material-symbols-outlined text-base">
              {tab === 'cv' ? 'description' : tab === 'linkedin' ? 'share' : 'compare_arrows'}
            </span>
            {tab === 'cv' ? 'CV' : tab === 'linkedin' ? 'LinkedIn' : 'Compare'}
          </button>
        ))}
      </div>

      {/* Main grid */}
      <div className="grid grid-cols-1 gap-6 sm:gap-8 lg:grid-cols-[320px_1fr]">
        {/* Left sidebar */}
        <div data-tour="tour-cv-upload" className="space-y-4 sm:space-y-5">
          {tabConfig && (
            <div className="rounded-2xl border border-primary/20 bg-primary/5 p-5">
              <p className="mb-2 text-[10px] font-bold uppercase tracking-[0.2em] text-primary">{tabConfig.badge}</p>
              <h2 className="text-xl font-semibold text-on-surface">{tabConfig.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-on-surface-variant">{tabConfig.description}</p>
            </div>
          )}

          {activeTab === 'cv' && (
            stateLoading ? <UploadPanelSkeleton /> : (
              <UploadPanel
                title={TAB_COPY.cv.uploadTitle}
                description={TAB_COPY.cv.uploadDescription}
                accept={TAB_COPY.cv.accept}
                hint={TAB_COPY.cv.hint}
                fileName={state?.cv.fileName}
                isPending={uploadResumeMutation.isPending}
                error={uploadResumeMutation.error instanceof Error ? uploadResumeMutation.error.message : undefined}
                onSelect={(file) => uploadResumeMutation.mutate(file)}
              />
            )
          )}

          {activeTab === 'linkedin' && (
            stateLoading ? <UploadPanelSkeleton /> : (
              <UploadPanel
                title={TAB_COPY.linkedin.uploadTitle}
                description={TAB_COPY.linkedin.uploadDescription}
                accept={TAB_COPY.linkedin.accept}
                hint={TAB_COPY.linkedin.hint}
                fileName={state?.linkedin.fileName}
                isPending={uploadLinkedinMutation.isPending}
                error={uploadLinkedinMutation.error instanceof Error ? uploadLinkedinMutation.error.message : undefined}
                onSelect={(file) => uploadLinkedinMutation.mutate(file)}
              />
            )
          )}

          {activeTab === 'compare' && (
            <div className="rounded-2xl bg-surface-container-low p-5 border border-outline-variant/10 space-y-4">
              <p className="text-[10px] font-bold uppercase tracking-widest text-primary">Compare Mode</p>
              <p className="text-sm text-on-surface-variant">See how your CV and LinkedIn profile align. Spot inconsistencies and gaps.</p>
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-on-surface-variant">CV</span>
                  <span className={`font-medium ${state?.cv.fileName ? 'text-emerald-400' : 'text-on-surface-variant'}`}>
                    {state?.cv.fileName ? 'Uploaded' : 'Missing'}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-on-surface-variant">LinkedIn</span>
                  <span className={`font-medium ${state?.linkedin.fileName ? 'text-emerald-400' : 'text-on-surface-variant'}`}>
                    {state?.linkedin.fileName ? 'Uploaded' : 'Missing'}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* Active State */}
          {activeTab !== 'compare' && (
            <div className="rounded-2xl bg-surface-container-low p-5 border border-outline-variant/10">
              <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-on-surface-variant">Active State</p>
              <div className="space-y-3 text-sm text-on-surface-variant">
                <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                  <span className="shrink-0">Current file</span>
                  <span className="truncate font-medium text-on-surface sm:text-right">
                    {currentAnalysis.fileName ?? 'Nothing uploaded yet'}
                  </span>
                </div>
                <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                  <span className="shrink-0">Analysis mode</span>
                  <span className="font-medium text-on-surface sm:text-right">Automatic on update</span>
                </div>
                <div className="flex flex-col gap-0.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                  <span className="shrink-0">Last analysis</span>
                  <span className="font-medium text-on-surface sm:text-right">
                    {currentAnalysis.reviewedAt
                      ? new Date(currentAnalysis.reviewedAt).toLocaleString()
                      : 'Pending first upload'}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Right content */}
        <div>{renderContent()}</div>
      </div>
    </div>
  );
}
